import React, { useCallback, useMemo } from 'react';

import { useObservable } from '~/@components/@hooks';
import {
    ISochiTableHeadCellProps,
    SochiSortSection,
    SochiStringFilter,
    SochiTableHeadCell,
} from '~/@sochi-components/SochiTable/SochiTableHeadCell';
import { SochiDateFilter } from '~/@sochi-components/SochiTable/SochiTableHeadCell/SochiDateFilter';
import { ProjectTimeSpentFilterField, projectTimeSpentStore } from '~/@user-store/timeSpent/timeSpentStore';
import { SortInput } from '~/graphql';
import i18n from '~/i18n';

type Props = {
    fieldName: ProjectTimeSpentFilterField;
    withFilter?: boolean;
    withSort?: boolean;
} & Omit<ISochiTableHeadCellProps, 'sortDirection' | 'children'>;

export const TimeSpentTableHeadCell = ({ fieldName, withFilter, withSort, ...restProps }: Props) => {
    const sortValue = useObservable(() => projectTimeSpentStore.sort.value);
    const setSortValue = useCallback((v: SortInput) => projectTimeSpentStore.sort.setValue(v), []);

    const sortLabels = useMemo(() => {
        switch (fieldName) {
            case ProjectTimeSpentFilterField.dumpLoadNumber:
            case ProjectTimeSpentFilterField.hoursSpent:
            case ProjectTimeSpentFilterField.co2:
                return i18n.SortLowHigh;
            case ProjectTimeSpentFilterField.date:
                return i18n.SortDate;
            default:
                return i18n.SortString;
        }
    }, [fieldName]);

    const sortSection = useMemo(() => {
        if (!withSort) return null;

        return <SochiSortSection fieldName={fieldName} sort={sortValue} setSort={setSortValue} labels={sortLabels} />;
    }, [withSort, fieldName, sortValue, setSortValue, sortLabels]);

    const filterSection = useMemo(() => {
        if (!withFilter) return null;

        switch (fieldName) {
            case ProjectTimeSpentFilterField.dumpLoadNumber:
            case ProjectTimeSpentFilterField.userName:
                return (
                    <SochiStringFilter
                        valueGetter={() => projectTimeSpentStore.filter.values[fieldName]}
                        setValue={v => projectTimeSpentStore.filter.setValue(fieldName, v)}
                    />
                );
            case ProjectTimeSpentFilterField.date:
                return (
                    <SochiDateFilter
                        valueGetter={() => projectTimeSpentStore.filter.values[fieldName]}
                        setValue={v => projectTimeSpentStore.setDateFilterValue(v)}
                    />
                );
            case ProjectTimeSpentFilterField.endDate:
                return (
                    <SochiDateFilter
                        valueGetter={() => projectTimeSpentStore.filter.values[fieldName]}
                        setValue={v => projectTimeSpentStore.setEndDateFilterValue(v)}
                    />
                );
            default:
                return null;
        }
    }, [withFilter, fieldName]);

    return (
        <SochiTableHeadCell {...restProps} sortDirection={sortValue?.field === fieldName ? sortValue.direction : null}>
            {sortSection}
            {filterSection}
        </SochiTableHeadCell>
    );
};
